import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { Search, MessageSquare, Plus, X, Upload, Trash2 } from "lucide-react";
import { RelationBadge, CommitmentBadge } from "@/components/Badges";
import { contactColor, relTime, CHANNEL_ICONS, formatDate, initials, gravatarUrl } from "@/lib/utils-fos";
import { toast } from "sonner";
import { KebabMenu } from "@/components/KebabMenu";
import { CsvImportModal } from "@/components/CsvImportModal";
import { ReachOutModal } from "@/pages/Inbox";

export default function People() {
  const [tab, setTab] = useState("people");
  const [people, setPeople] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    const [pp, cc, pr, ev] = await Promise.all([
      endpoints.people(),
      endpoints.commitments(),
      endpoints.projects(),
      endpoints.events(),
    ]);
    setPeople(pp);
    setCommitments(cc);
    setProjects(pr);
    setEvents(ev);
  };
  useEffect(() => {
    load();
  }, []);

  const deletePerson = async (person) => {
    if (!window.confirm(`Remove ${person.name} from People? This cannot be undone.`)) return;
    try {
      await endpoints.deletePerson(person.id);
      setPeople((prev) => prev.filter((p) => p.id !== person.id));
      setSelected(null);
      toast.success(`${person.name} removed`);
    } catch (e) {
      toast.error("Could not delete person");
    }
  };

  const commitByPerson = useMemo(() => {
    const map = {};
    for (const c of commitments) {
      if (c.status !== "done") {
        map[c.from_person_id] = (map[c.from_person_id] || 0) + 1;
        if (c.to_person_id !== c.from_person_id) map[c.to_person_id] = (map[c.to_person_id] || 0) + 1;
      }
    }
    return map;
  }, [commitments]);

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.role?.toLowerCase().includes(q) ||
        p.company?.toLowerCase().includes(q)
    );
  }, [search, people]);

  const openPerson = (p) => {
    setSelected(p);
    setDraft("");
  };

  const doDraft = async (person) => {
    setDraftLoading(true);
    try {
      const r = await endpoints.draftMessage(person.id, "follow_up");
      setDraft(r.message || "");
    } catch (e) {
      toast.error("Draft failed");
    } finally {
      setDraftLoading(false);
    }
  };

  const markCommitmentDone = async (c) => {
    await endpoints.updateCommitment(c.id, { status: "done", resolved_at: new Date().toISOString() });
    setCommitments(commitments.map((x) => (x.id === c.id ? { ...x, status: "done" } : x)));
    toast.success("Commitment closed");
  };

  // ReachOutModal state — used both by the AI-drafted "Send now" button in
  // PersonDrawer and by follow-ups on individual commitments listed in the
  // drawer. Commitments now live on their own /commitments page; the tab was
  // removed from this page in Feb 2026.
  const [followUpInitial, setFollowUpInitial] = useState(null);

  const openAiDraftSendNow = (person, body) => {
    if (!body?.trim()) return;
    const platform = (person?.preferred_channel || person?.communication_preference || "email").toLowerCase();
    setFollowUpInitial({
      platform: ["email", "slack", "whatsapp", "linkedin", "twitter", "sms"].includes(platform) ? platform : "email",
      to_person_id: person?.id || "",
      to_display: person?.email || "",
      subject: "",
      body: body.trim(),
    });
  };

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-7xl mx-auto">
      {/* Tabs — Commitments tab was moved to its own /commitments page in Feb 2026. */}
      <div className="flex items-center gap-2 mb-6 border-b pb-0" style={{ borderColor: "#E5E5E5" }}>
        <TabButton active={tab === "people"} onClick={() => setTab("people")} testid="tab-people">
          People
        </TabButton>
        <TabButton active={tab === "context"} onClick={() => setTab("context")} testid="tab-context">
          Founder context
        </TabButton>
      </div>

      {tab === "context" && <FounderContextTab />}

      {tab === "people" && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 fos-card flex items-center gap-2 px-3 py-2">
              <Search size={14} style={{ color: "#737373" }} />
              <input
                data-testid="people-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search people…"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-md"
              style={{ background: "#0A0A0A", color: "#FFFFFF" }}
              data-testid="add-person-btn"
            >
              <Plus size={14} /> Person
            </button>
            <KebabMenu
              testid="people-kebab"
              items={[
                { label: "Import from CSV", icon: <Upload size={14} />, onClick: () => setShowImport(true), testid: "people-kebab-import" },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPeople.map((p) => (
              <div
                key={p.id}
                data-testid={`person-card-${p.id}`}
                onClick={() => openPerson(p)}
                className="fos-card fos-hover p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-start gap-3">
                  <Avatar url={p.avatar_url} name={p.name} email={p.email} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-semibold text-sm truncate">{p.name}</p>
                    </div>
                    <p className="text-xs truncate" style={{ color: "#737373" }}>
                      {p.role} · {p.company}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <RelationBadge type={p.relationship_type} />
                      {commitByPerson[p.id] > 0 && (
                        <span className="text-[10px] mono px-1.5 py-0.5 rounded" style={{ background: "#E5E5E5", color: "#0A0A0A" }}>
                          {commitByPerson[p.id]} open
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] mono mt-2" style={{ color: contactColor(p.last_contact_at) }}>
                      ● Last contact {relTime(p.last_contact_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "commitments" && (
        <div className="fos-card p-6 text-center text-sm text-neutral-500">
          Commitments moved. See the <a href="/commitments" className="fos-inline-link">Commitments</a> page in the sidebar.
        </div>
      )}

      {followUpInitial && (
        <ReachOutModal
          initial={followUpInitial}
          people={people}
          onClose={() => setFollowUpInitial(null)}
          onSent={() => setFollowUpInitial(null)}
        />
      )}

      {selected && (
        <PersonDrawer
          person={selected}
          onClose={() => setSelected(null)}
          onDraft={() => doDraft(selected)}
          onDelete={() => deletePerson(selected)}
          onSendDraft={(body) => openAiDraftSendNow(selected, body)}
          draft={draft}
          draftLoading={draftLoading}
          commitments={commitments.filter((c) => c.from_person_id === selected.id || c.to_person_id === selected.id)}
          events={events.filter((e) => e.people_ids?.includes(selected.id))}
          projects={projects}
          people={people}
        />
      )}
      {showAdd && (
        <AddPersonModal onClose={() => setShowAdd(false)} onCreated={(np) => { setPeople([np, ...people]); setShowAdd(false); toast.success("Person added"); }} />
      )}
      {showImport && (
        <CsvImportModal
          resource="people"
          onClose={() => setShowImport(false)}
          onSuccess={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

function TabButton({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
      style={{
        borderColor: active ? "#0A0A0A" : "transparent",
        color: active ? "#0A0A0A" : "#737373",
      }}
    >
      {children}
    </button>
  );
}

function Avatar({ url, name, email }) {
  const [src, setSrc] = React.useState(url || gravatarUrl(email, 96) || null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    setSrc(url || gravatarUrl(email, 96) || null);
    setFailed(false);
  }, [url, email]);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => {
          // If Gmail avatar fails and we haven't tried Gravatar yet, try Gravatar; else show initials.
          const g = gravatarUrl(email, 96);
          if (src !== g && g) setSrc(g);
          else setFailed(true);
        }}
        className="h-11 w-11 rounded-full object-cover shrink-0 border border-neutral-200"
      />
    );
  }
  return (
    <div className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 bg-neutral-200 text-neutral-800">
      {initials(name)}
    </div>
  );
}

function KanbanColumn() {
  // Removed Feb 2026 — Commitments now live at /commitments.
  return null;
}

function PersonDrawer({ person, onClose, onDraft, onDelete, onSendDraft, draft, draftLoading, commitments, events, projects, people }) {
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const linked = new Set();
  commitments.forEach((c) => c.project_id && linked.add(c.project_id));
  events.forEach((e) => (e.project_ids || []).forEach((pid) => linked.add(pid)));
  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div
        className="w-full max-w-lg h-full overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#FFFFFF", borderLeft: "1px solid #E5E5E5" }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">{person.name}</h2>
          <div className="flex items-center gap-1">
            {onDelete && (
              <button
                data-testid="person-drawer-delete"
                onClick={onDelete}
                className="p-1 rounded hover:bg-red-50 text-red-600"
                title="Delete person"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-white/5">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex items-start gap-3 mb-5">
          <Avatar url={person.avatar_url} name={person.name} email={person.email} />
          <div className="flex-1 min-w-0">
            <p className="text-sm">{person.role}</p>
            <p className="text-xs" style={{ color: "#737373" }}>{person.company}</p>
            <div className="mt-2"><RelationBadge type={person.relationship_type} /></div>
          </div>
        </div>

        <div className="fos-card p-3 mb-4">
          <p className="text-[10px] mono mb-2" style={{ color: "#737373" }}>CONTACT</p>
          <p className="text-sm">{person.email || "—"}</p>
          {person.whatsapp && <p className="text-sm">WA: {person.whatsapp}</p>}
          {person.slack_id && <p className="text-sm">Slack: {person.slack_id}</p>}
        </div>

        {(person.communication_preference || person.sensitivities) && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2" style={{ color: "#737373" }}>PREFERENCES & SENSITIVITIES</p>
            {person.communication_preference && (
              <p className="text-sm mb-1"><span className="mono text-[10px]" style={{ color: "#737373" }}>PREFERS · </span>{person.communication_preference}</p>
            )}
            {person.sensitivities && (
              <p className="text-sm" style={{ color: "#FF9F0A" }}>· {person.sensitivities}</p>
            )}
          </div>
        )}

        <button
          data-testid="reach-out-btn"
          onClick={onDraft}
          disabled={draftLoading}
          className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-md mb-4"
          style={{ background: "#0A0A0A", color: "#FFFFFF" }}
        >
          <MessageSquare size={14} />
          {draftLoading ? "Drafting…" : "Reach out (AI draft)"}
        </button>
        {draft && (
          <div className="fos-card p-3 mb-4" data-testid="ai-draft-output">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] mono" style={{ color: "#0A0A0A" }}>AI DRAFT</p>
              <button
                data-testid="ai-draft-send-now"
                onClick={() => onSendDraft?.(draft)}
                className="text-xs px-2.5 py-1 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5"
              >
                <MessageSquare size={11} /> Send now
              </button>
            </div>
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{draft}</pre>
          </div>
        )}

        {commitments.length > 0 && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2" style={{ color: "#737373" }}>COMMITMENTS ({commitments.length})</p>
            <div className="space-y-2">
              {commitments.map((c) => (
                <div key={c.id} className="text-sm flex items-center gap-2">
                  <CommitmentBadge status={c.status} />
                  <span className="flex-1 truncate">{c.what}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {events.length > 0 && (
          <div className="fos-card p-3 mb-4">
            <p className="text-[10px] mono mb-2" style={{ color: "#737373" }}>INTERACTION HISTORY</p>
            <div className="space-y-2">
              {events.slice(0, 6).map((e) => (
                <div key={e.id} className="text-sm flex items-start gap-2">
                  <span>{CHANNEL_ICONS[e.source_channel] || "•"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="leading-snug">{e.content_summary}</p>
                    <p className="text-[10px] mono" style={{ color: "#737373" }}>{formatDate(e.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {linked.size > 0 && (
          <div className="fos-card p-3">
            <p className="text-[10px] mono mb-2" style={{ color: "#737373" }}>LINKED PROJECTS</p>
            <div className="flex flex-wrap gap-1.5">
              {[...linked].map((id) => (
                <span
                  key={id}
                  className="text-[11px] mono px-2 py-0.5 rounded"
                  style={{ background: "#F5F5F5", color: "#0A0A0A" }}
                >
                  {projectName(id)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddPersonModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", role: "", company: "", email: "", relationship_type: "team" });
  const submit = async () => {
    if (!form.name.trim()) return;
    const p = await endpoints.createPerson(form);
    onCreated(p);
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="fos-card p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Add Person</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5"><X size={16} /></button>
        </div>
        {["name", "role", "company", "email"].map((k) => (
          <input
            key={k}
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            placeholder={k[0].toUpperCase() + k.slice(1)}
            className="w-full text-sm px-3 py-2 rounded-md border bg-transparent outline-none mb-2"
            style={{ borderColor: "#E5E5E5", color: "#0A0A0A" }}
            data-testid={`person-${k}`}
          />
        ))}
        <select
          value={form.relationship_type}
          onChange={(e) => setForm({ ...form, relationship_type: e.target.value })}
          className="w-full text-sm px-3 py-2 rounded-md border bg-transparent outline-none mb-3"
          style={{ borderColor: "#E5E5E5", color: "#0A0A0A", background: "#FFFFFF" }}
        >
          {["client", "team", "investor", "vendor", "partner", "founder"].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <button onClick={submit} className="w-full text-sm px-3 py-2 rounded-md font-medium" style={{ background: "#0A0A0A", color: "#FFFFFF" }}>
          Create
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// FounderContextTab — one card per founder-tagged person.
// Each card holds the fields the AI leans on when talking about / drafting for
// that founder: communication style, priorities, sensitivities, update
// frequency. Fields are stored on the Person doc (people collection) so they
// travel with the person and stay multi-tenant. Timezone lives on the
// workspace-level `settings` collection (there's only one "you" per
// workspace).
// -----------------------------------------------------------------------------
function FounderContextTab() {
  const [people, setPeople] = useState([]);
  const [settings, setSettings] = useState([]);

  const load = async () => {
    const [pp, ss] = await Promise.all([endpoints.people(), endpoints.settings()]);
    setPeople(pp);
    setSettings(ss);
  };
  useEffect(() => { load(); }, []);

  const founders = useMemo(
    () => people.filter((p) => (p.relationship_type || "").toLowerCase() === "founder"),
    [people],
  );

  const settingBag = Object.fromEntries(settings.map((s) => [s.key, s]));
  const setSettingKey = async (key, value) => {
    const existing = settingBag[key];
    if (existing) await endpoints.updateSetting(existing.id, { value });
    else await endpoints.createSetting({ key, value });
    load();
    toast.success("Saved");
  };

  const updateFounderField = async (personId, field, value) => {
    await endpoints.updatePerson(personId, { [field]: value });
    setPeople((prev) => prev.map((p) => (p.id === personId ? { ...p, [field]: value } : p)));
    toast.success("Saved");
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="founder-context-panel">
      <section className="fos-card p-5">
        <h2 className="text-sm font-semibold mb-1">Workspace</h2>
        <p className="text-xs text-neutral-500 mb-4">
          Applies across every founder card below.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ContextField
            label="Timezone"
            value={settingBag.timezone?.value}
            onSave={(v) => setSettingKey("timezone", v)}
            testid="tz"
          />
        </div>
      </section>

      {founders.length === 0 ? (
        <section className="fos-card p-8 text-center" data-testid="founder-context-empty">
          <p className="text-sm text-neutral-600 mb-1">No founders added yet.</p>
          <p className="text-xs text-neutral-500">
            Tag a person as <span className="font-medium">Founder</span> on the People tab
            and their context card will appear here.
          </p>
        </section>
      ) : (
        founders.map((f) => (
          <FounderCard
            key={f.id}
            founder={f}
            onSave={(field, value) => updateFounderField(f.id, field, value)}
          />
        ))
      )}
    </div>
  );
}

function FounderCard({ founder, onSave }) {
  return (
    <section className="fos-card p-5" data-testid={`founder-card-${founder.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-semibold text-neutral-700">
          {initials(founder.name || "?")}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900 truncate">{founder.name}</h3>
          <p className="text-xs text-neutral-500 truncate">
            {founder.role || "Founder"}{founder.company ? ` · ${founder.company}` : ""}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ContextField
          label="Communication style"
          value={founder.communication_preference}
          onSave={(v) => onSave("communication_preference", v)}
          testid={`founder-style-${founder.id}`}
          fullWidth
        />
        <ContextField
          label="Priorities"
          value={founder.priorities}
          onSave={(v) => onSave("priorities", v)}
          testid={`founder-priorities-${founder.id}`}
          fullWidth
        />
        <ContextField
          label="Sensitivities"
          value={founder.sensitivities}
          onSave={(v) => onSave("sensitivities", v)}
          testid={`founder-sensitivities-${founder.id}`}
          fullWidth
        />
        <ContextField
          label="Update frequency"
          value={founder.update_frequency}
          onSave={(v) => onSave("update_frequency", v)}
          testid={`founder-freq-${founder.id}`}
        />
      </div>
    </section>
  );
}

function ContextField({ label, value, onSave, testid, fullWidth }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      <input
        data-testid={testid}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => local !== (value || "") && onSave(local)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(local); } }}
        className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white text-neutral-900 outline-none focus:border-neutral-500"
      />
    </div>
  );
}
